import { Box, Text, Stack, StackProps, Button } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { FormsIcon } from '../../../icons';
import { BoardTemplateLocalListItemType, LocalTemplateType } from '../types';
import { TemplateType } from '../../../types';
import { DYNAMIC_URLS } from '../../../consts';
import { RelatedForm } from '../../../types/templates/TemplateType';

type Templates =
  | LocalTemplateType
  | TemplateType
  | BoardTemplateLocalListItemType;

export type TemplateWithRelatedFormsProps = StackProps & {
  templates: Templates[];
};

/**
 * TemplateWithRelatedForms component to display a list of templates that have related forms.
 * This component is used when user try to delete a template.
 *
 * @param templates - Array of templates to check for related forms
 * @param stackProps - Additional props to pass to the Stack component
 */
export const TemplateWithRelatedForms: React.FC<
  TemplateWithRelatedFormsProps
> = ({ templates, ...stackProps }) => {
  const toShowItems = 5;
  const [showAll, setShowAll] = useState(false);
  const { t } = useTranslation(['templates']);
  const templatesWithRelatedForms = templates.filter(
    template => template.hasRelatedForms,
  );
  const relatedForms = templatesWithRelatedForms.reduce<RelatedForm[]>(
    (acc, template) => {
      if (template.relatedForms) {
        return [...acc, ...template.relatedForms];
      }
      return acc;
    },
    [],
  );

  return templates.some(template => template.hasRelatedForms) ? (
    <Stack spacing={16} mt={24} {...stackProps}>
      <Stack spacing={4}>
        <Text fw={600}>{t('relatedForms.title')}</Text>
        <Text fw={500}>{t('relatedForms.delete')}</Text>
      </Stack>

      <Stack>
        {relatedForms.slice(0, showAll ? undefined : toShowItems).map(form => (
          <Box
            key={form.id}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <FormsIcon />
            <Text
              target="_blank"
              variant="link"
              component={Link}
              to={DYNAMIC_URLS.formEdit({
                formId: form.id,
              })}
            >
              {form.name}
            </Text>
          </Box>
        ))}
        {relatedForms.length > toShowItems && (
          <Stack>
            <Box pl={16}>
              <Button variant="subtle" onClick={() => setShowAll(s => !s)}>
                {t(showAll ? 'common:showLess' : 'common:showMore')}
                {!showAll && ` (${relatedForms.length - toShowItems})`}
              </Button>
            </Box>
          </Stack>
        )}
      </Stack>
    </Stack>
  ) : null;
};
